import { createContext, useContext, FC, useState, useCallback, ReactNode, useEffect, useMemo } from "react";
import { LoadingPage } from "@app/components/common/LoadingPage";
import { getEarlyTranslation } from "@app/core/i18n/languageFromBrowser";

// toggle to show/hide loading phase messages globally — flip to false to keep only the spinner
export const SHOW_LOADING_MESSAGES = true;

// single source of truth for all loading sources. add/remove a source by editing this record only
// declaration order is the priority order within each scope (first active source wins the message)
// scope "boot" → full-screen overlay (no chrome yet); "content" → only the page content area
const SOURCES = {
    config: { scope: "boot", messageKey: "loading.configuration", initialLoading: false },
    auth: { scope: "boot", messageKey: "loading.auth", initialLoading: true },
    user: { scope: "boot", messageKey: "loading.user", initialLoading: false },
    hierarchy: { scope: "boot", messageKey: "loading.hierarchy", initialLoading: false },
    agentDetails: { scope: "boot", messageKey: "loading.agentDetails", initialLoading: false },
    policy: { scope: "content", messageKey: "loading.content", initialLoading: false },
    chunk: { scope: "content", messageKey: "loading.content", initialLoading: false },
} as const;

export type LoadingSource = keyof typeof SOURCES;

type LoadingRegistration = {
    isLoading: boolean;
    message?: string;
};

// split into two contexts so that components that only register loading state
// (via useRegisterLoading) do not re-render when the loading state itself changes —
// only the overlay (which reads via useLoadingState) needs to re-render on state changes.
// without this split, any setLoading() call cascades re-renders through every consumer,
// which can trigger feedback loops with libraries like split.io that re-evaluate on parent re-renders
type LoadingActions = {
    setLoading: (source: LoadingSource, loading: boolean, message?: string) => void;
};
type LoadingState = {
    isContentLoading: boolean;
    contentMessage: string | undefined;
};

const LoadingActionsContext = createContext<LoadingActions | null>(null);
const LoadingStateContext = createContext<LoadingState | null>(null);

export const useLoadingState = (): LoadingState => {
    const context = useContext(LoadingStateContext);
    if (!context) {
        throw new Error("useLoadingState must be used within LoadingProvider");
    }
    return context;
};

// hook for components to register their loading state with optional message
export const useRegisterLoading = (source: LoadingSource, isLoading: boolean, message?: string): void => {
    const actions = useContext(LoadingActionsContext);
    if (!actions) {
        throw new Error("useRegisterLoading must be used within LoadingProvider");
    }
    const { setLoading } = actions;

    useEffect(() => {
        setLoading(source, isLoading, message);
    }, [source, isLoading, message, setLoading]);

    // cleanup on unmount
    useEffect(() => {
        return () => {
            setLoading(source, false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source]);
};

const SOURCE_KEYS = Object.keys(SOURCES) as LoadingSource[];
// boot sources gate the chrome (header/sidebar). while any are loading, the full-screen overlay shows
const BOOT_PRIORITY = SOURCE_KEYS.filter(k => SOURCES[k].scope === "boot");
// content sources fire after the chrome is up. only the content area is covered, header and sidebar stay visible
const CONTENT_PRIORITY = SOURCE_KEYS.filter(k => SOURCES[k].scope === "content");

const defaultMessages = Object.fromEntries(
    SOURCE_KEYS.map(k => [k, getEarlyTranslation(SOURCES[k].messageKey)])
) as Record<LoadingSource, string>;

const initialRegistrations = Object.fromEntries(
    SOURCE_KEYS.map(k => [k, { isLoading: SOURCES[k].initialLoading }])
) as Record<LoadingSource, LoadingRegistration>;

type Props = {
    children: ReactNode;
    // override per-source initial state — used by tests to suppress the default auth-loading overlay
    initialRegistrationOverrides?: Partial<Record<LoadingSource, LoadingRegistration>>;
    // skip rendering the global LoadingPage element — used by tests so the hidden overlay div doesn't pollute DOM queries
    disableOverlay?: boolean;
};

export const LoadingProvider: FC<Props> = ({ children, initialRegistrationOverrides, disableOverlay = false }) => {
    const [registrations, setRegistrations] = useState<Record<LoadingSource, LoadingRegistration>>(() => ({
        ...initialRegistrations,
        ...initialRegistrationOverrides,
    }));

    const setLoading = useCallback((source: LoadingSource, loading: boolean, message?: string) => {
        setRegistrations(prev => {
            if (prev[source].isLoading === loading && prev[source].message === message) {
                return prev;
            }
            return { ...prev, [source]: { isLoading: loading, message } };
        });
    }, []);

    // first active loading source per scope wins the displayed message
    const { isBootLoading, bootMessage, isContentLoading, contentMessage } = useMemo(() => {
        const activeBootSource = BOOT_PRIORITY.find(s => registrations[s].isLoading);
        const activeContentSource = CONTENT_PRIORITY.find(s => registrations[s].isLoading);
        return {
            isBootLoading: !!activeBootSource,
            bootMessage: activeBootSource
                ? (registrations[activeBootSource].message ?? defaultMessages[activeBootSource])
                : undefined,
            isContentLoading: !!activeContentSource,
            contentMessage: activeContentSource
                ? (registrations[activeContentSource].message ?? defaultMessages[activeContentSource])
                : undefined,
        };
    }, [registrations]);

    const actions = useMemo(() => ({ setLoading }), [setLoading]);
    const state = useMemo(() => ({ isContentLoading, contentMessage }), [isContentLoading, contentMessage]);

    return (
        <LoadingActionsContext.Provider value={actions}>
            <LoadingStateContext.Provider value={state}>
                {!disableOverlay && <LoadingPage loading={isBootLoading} loadingMessage={bootMessage} />}
                {children}
            </LoadingStateContext.Provider>
        </LoadingActionsContext.Provider>
    );
};
